import { drawing, geometry } from '@progress/kendo-drawing';
import { ChartLegendProps, LegendItemVisualArgs } from '@progress/kendo-react-charts';
import { Datum, GroupedList, LegendDatum, ListResponse } from 'common/store/api/chart.api';
import { formatAppDate, formatNumber, NumberFormat, percentage } from '../../../common/hooks/utils';
import {
  BarChartData,
  BarChartVisualDisplayType,
  BarChartSeriesSortOrder,
  BarChartValueType,
  KwelloBarChartOptions,
  LineChartData,
} from 'common/models/chart.model';
import { AggregateType, ChartLabels } from '../../models/widget.model';
import { EXCEL_EXPORT_DATE_FORMAT, ISO_DATE_REGEX } from 'common/constants';

export const FONT_SMALL = '400 14px Inter';
export const FONT_LARGE = '500 14px Inter';
export const CHART_DISPLAY_PERCENTAGE_FORMAT: NumberFormat = 'toFixedOne';
export const CHART_EXPORT_PERCENTAGE_FORMAT: NumberFormat = 'absolute';

export const getCssVar = (cssVar: string): string => {
  return getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
};

export interface DatumObject {
  [key: string]: {
    [key: string]: string | number;
  };
}

export const datumToObjectTransformer = (originalData: Datum): DatumObject => {
  const result: DatumObject = {};

  originalData.data?.forEach((channel) => {
    const channelType = channel.x;
    result[channelType] = {};

    channel.data?.forEach((person) => {
      result[channelType][person.x] = person.y;
    });
  });

  return result;
};

export interface PieChartSeriesItem {
  x: string;
  y: number;
  value: number;
  sortOrder: number;
}

interface PieChartData {
  series: PieChartSeriesItem[];
  seriesLabel: string;
  total: number;
}

export const buildPieChartDataFromSeries = (data: GroupedList[]): PieChartData => {
  const total: number = data[0].y;
  const seriesLabel: string = data[0].x;
  const series = data[0].data
    .map((seriesItem) => ({
      x: seriesItem.x,
      y: seriesItem.y,
      value: percentage(seriesItem.y, total),
      sortOrder: seriesItem.sortOrder,
    }))
    .sort((a, b) => b.y - a.y);

  return {
    series,
    seriesLabel,
    total,
  };
};

interface ExportPieChartDataExcelItem {
  Value: number;
  Percentage: string;
  [key: string]: string | number;
}

export const exportPieChartDataExcel = (
  data: PieChartData,
  label: string
): ExportPieChartDataExcelItem[] => {
  return data.series.map((seriesItem) => ({
    [label]: seriesItem.x,
    Value: seriesItem.y,
    Percentage: `${seriesItem.value.toString()}%`,
  }));
};

interface BuildLineChartDataFromSeriesArgs {
  groupedList: GroupedList[];
  labels?: ChartLabels;
}

export const buildLineChartDataFromSeries = ({
  groupedList,
  labels,
}: BuildLineChartDataFromSeriesArgs): LineChartData => {
  // Return default names as labels if no labels are provided
  const seriesLabels =
    labels ??
    groupedList.reduce((acc: ChartLabels, item) => {
      acc[item.x] = item.x;
      return acc;
    }, {});

  const dataArrays = groupedList
    .map((seriesItem) => (seriesItem.data ? seriesItem.data : []))
    .flat();
  const categories = [...dataArrays]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .reduce((acc: string[], item) => {
      if (!acc.includes(item.x)) {
        acc.push(item.x);
      }
      return acc;
    }, []);

  const series = groupedList
    .filter((seriesItem) => !!seriesItem.x)
    .map((seriesItem) => ({
      name: seriesItem.x,
      data: [...seriesItem.data]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((dataItem) => {
          const aggregateValue = dataItem.aggregates?.find(
            (aggregate) => aggregate.name === seriesItem.x
          )?.value;
          return aggregateValue ?? dataItem.y;
        }),
    }));

  return { categories: categories, series: series, labels: seriesLabels };
};

export const exportLineChartDataExcel = (data: LineChartData) => {
  const { series, categories, labels } = data;

  return categories.map((category, categoryIndex) => ({
    Date: formatAppDate(category as string, EXCEL_EXPORT_DATE_FORMAT),
    ...series.reduce((acc, seriesItem) => {
      return {
        ...acc,
        [labels[seriesItem.name] ?? seriesItem.name]: seriesItem.data[categoryIndex],
      };
    }, {}),
  }));
};

interface BuildBarChartDataFromSeriesOptions {
  tooltipPallet?: string[];
  reversed?: boolean;
  valueType?: KwelloBarChartOptions['valueType'];
  seriesSortOrder?: KwelloBarChartOptions['seriesSortOrder'];
  seriesValueDisplayType?: BarChartVisualDisplayType | BarChartVisualDisplayType[]; // Display the values in the segments, or the total
  formatCategoryLabel?: (item: Datum) => string;
}

export const newBuildBarChartDataFromSeries = (
  data: GroupedList[],
  categoryLegendData: LegendDatum[],
  groupBy: string,
  {
    valueType = 'percentage',
    formatCategoryLabel,
    seriesSortOrder = 'totalValue',
    seriesValueDisplayType = 'total',
  }: BuildBarChartDataFromSeriesOptions = {}
): BarChartData => {
  const total = data[0].y ?? data[0].data.reduce((acc, item) => acc + item.y, 0);

  const categories = data[0].data
    .map((item) => ({
      id: item.x.toString(),
      name: formatCategoryLabel
        ? formatCategoryLabel(item)
        : item.alignmentLabel ?? item.x.toString(),
      tooltip: item.tooltip,
      sortOrder: item.sortOrder,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const categoriesDataCount = data[0].data.reduce((acc: { [key: string]: number }, item) => {
    acc[item.x.toString()] = item.y;
    return acc;
  }, {});

  const seriesItems = data[0].data.reduce((acc: { [key: string]: any }, item) => {
    item.data?.forEach((dataSeriesItem) => {
      const name = dataSeriesItem.alignmentLabel ?? dataSeriesItem.x.toString();

      if (!acc[name]) {
        acc[name] = {
          id: dataSeriesItem.x.toString(),
          name: name,
          totalValue: dataSeriesItem.y,
          color1: dataSeriesItem.alignmentColor1,
          color2: dataSeriesItem.alignmentColor2,
          sortOrder: dataSeriesItem.sortOrder,
          tooltipText: dataSeriesItem.tooltip,
        };
      } else {
        acc[name].totalValue += dataSeriesItem.y;
      }
    });

    return acc;
  }, {});

  const orderedSeriesItems = Object.values(seriesItems).sort((a, b) => {
    const sortOrder = seriesSortOrder as BarChartSeriesSortOrder;
    return sortOrder === 'sortOrder'
      ? (a[sortOrder] as number) - (b[sortOrder] as number)
      : (b[sortOrder] as number) - (a[sortOrder] as number);
  });

  const series = orderedSeriesItems.map((seriesItem) => ({
    aggregates: [],
    name: seriesItem.name,
    color1: seriesItem.color1,
    color2: seriesItem.color2,
    tooltipText: seriesItem.tooltipText,
    dataCount: seriesItems[seriesItem.name].totalValue,
    data: categories.map((category) => {
      const value =
        data[0].data
          .find((dataItem) => dataItem.x.toString() === category.id)
          ?.data?.find((categoryDataItem) => categoryDataItem.x.toString() === seriesItem.id)?.y ??
        0;

      return {
        categoryId: category.id,
        dataCount: value,
        value: valueType === 'percentage' ? percentage(value ?? 0, total) : value ?? 0,
        displayValue:
          seriesValueDisplayType === 'segment' && valueType === 'percentage'
            ? percentage(value, categoriesDataCount[category.id])
            : undefined,
        aggregates: [],
      };
    }),
  }));

  const singleSeries =
    series.length === 0
      ? [
          {
            aggregates: [],
            name: data[0].x ?? '',
            dataCount: data[0].y,
            data: Object.keys(categoriesDataCount)?.map((key) => ({
              categoryId: key,
              dataCount: categoriesDataCount[key],
              displayValue: 100,
              value: categoriesDataCount[key],
              aggregates: [],
            })),
          },
        ]
      : undefined;

  return {
    categories,
    categoriesDataCount,
    series: singleSeries ?? series,
    seriesLabel: groupBy,
    total,
    valueType: valueType as BarChartValueType,
  };
};

export interface ExportBarChartDataExcelOptions {
  flipAxis?: boolean;
  percentageFormat?: NumberFormat;
  valueType?: KwelloBarChartOptions['valueType'];
  showCategoryTotalPercentage?: boolean;
}

interface ExportBarChartDataExcelArgs {
  data: BarChartData;
  label: string;
  options: ExportBarChartDataExcelOptions;
}

export const exportBarChartDataExcel = ({
  data,
  label,
  options = {},
}: ExportBarChartDataExcelArgs): any => {
  const { flipAxis, valueType = 'percentage', showCategoryTotalPercentage } = options;
  const { categoriesDataCount } = data;

  if (flipAxis) {
    return data.series.map((seriesItem) => {
      return {
        [label]: seriesItem.name,
        ...data.categories.reduce((acc, category) => {
          return {
            ...acc,
            [category.name]: `${seriesItem.data.find((dataItem) => dataItem.categoryId === category.id)?.value.toString()}`,
          };
        }, {}),
      };
    });
  }

  return data.categories.map((category) => {
    const idStr = category.id.toString();
    const isDateCategory = ISO_DATE_REGEX.test(idStr) && !isNaN(Date.parse(idStr));

    return {
      [label]: isDateCategory
        ? formatAppDate(category.id.toString(), EXCEL_EXPORT_DATE_FORMAT)
        : category.name,
      ...data.series.reduce((acc, seriesItem) => {
        const total = showCategoryTotalPercentage
          ? seriesItem.data.reduce((acc, item) => acc + item.dataCount, 0)
          : categoriesDataCount[category.id.toString()];

        const value =
          valueType === 'percentage'
            ? percentage(
                seriesItem.data.find((dataItem) => dataItem.categoryId === category.id)
                  ?.dataCount ?? 0,
                total
              )
            : seriesItem.data.find((dataItem) => dataItem.categoryId === category.id)?.value;

        const updatedValue =
          valueType === 'percentage' && (value === 0 || isNaN(value ?? 0)) ? '' : value;

        return {
          ...acc,
          [seriesItem.name]: value ?? '',
          [seriesItem.name]: updatedValue ?? '',
        };
      }, {}),
    };
  });
};

export const formatChartSeriesPercentageLabel = (value: number): string => {
  return formatNumber(value, CHART_DISPLAY_PERCENTAGE_FORMAT).toString();
};

export function padStrings(strings: string[]) {
  // Find the maximum length among all strings
  const maxLength = strings.reduce((max, str) => Math.max(max, str.length), 0);

  // Iterate through each string and add spaces to make them equal in length
  const paddedStrings = strings.map((str) => {
    const paddingLength = maxLength - str.length;
    const padding = '.'.repeat(paddingLength); // Creates a string of spaces of length paddingLength
    return str + padding;
  });

  return paddedStrings;
}

export function showNumberWithSign(num: number) {
  if (num > 0) {
    return '+' + num.toString();
  } else if (num < 0) {
    return '-' + Math.abs(num).toString();
  } else {
    return '0'; // If number is zero, return "0"
  }
}

export const DEFAULT_CHART_COLORS = [
  getCssVar('--kendo-color-series-a'),
  getCssVar('--kendo-color-series-b'),
  getCssVar('--kendo-color-series-c'),
  getCssVar('--kendo-color-series-d'),
  getCssVar('--kendo-color-series-e'),
  getCssVar('--kendo-color-series-f'),
  getCssVar('--kendo-color-series-g'),
  getCssVar('--kendo-color-series-h'),
  getCssVar('--kendo-color-series-i'),
  getCssVar('--kendo-color-series-j'),
];

export const circleLegentItem = (
  e: LegendItemVisualArgs,
  labelOverride?: string
): drawing.Element => {
  const color = e.options.markers.background;
  const labelColor = e.options.labels.color;
  const rect = new geometry.Rect([0, 0], [1000, 50]);
  const layout = new drawing.Layout(rect, {
    spacing: 5,
    alignItems: 'center',
  });

  const CircGeometry = new geometry.Circle([10, 5], 3);
  const MarkerCircle = new drawing.Circle(CircGeometry, {
    stroke: { color: color, width: 1 },
    fill: { color: color },
  });
  const labelText: string =
    e.series.type === 'donut' ? e.series.data[e.pointIndex].x : e.series.name;
  const label = new drawing.Text(labelOverride ?? labelText, [0, 0], {
    fill: {
      color: labelColor,
    },
    font: '400 12px Inter',
  });
  layout.append(MarkerCircle, label);
  layout.reflow();

  return layout;
};

export const legendProps: ChartLegendProps = {
  position: 'bottom',
  align: 'center',
  item: { visual: circleLegentItem },
};

export const formatElementId = (text: string): string =>
  text.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

/**
 * Formats the given pathname by removing the third segment of the path.
 *
 * @param pathname - The original pathname string to be formatted.
 * @returns The formatted pathname with the third segment removed.
 */
export const formatExportPathname = (pathname: string): string => {
  const path = pathname.split('/');
  return path.filter((_, index) => index !== 2).join('/');
};

// Allocate totals for treeviews from aggregates
export const allocateAggregateTotals = (dataResult: ListResponse, aggregates: string[]) => {
  const result: { [key: string]: number } = aggregates.reduce(
    (acc: { [key: string]: number }, curr: string): { [key: string]: number } => (
      (acc[curr] = 0), acc
    ),
    {}
  );
  return dataResult.data.reduce(
    (accumulator: { [key: string]: number }, currentValue: any): { [key: string]: number } => {
      aggregates.forEach((aggregateName: string) => {
        const queryAggregate = currentValue.aggregates?.find(
          (agg: AggregateType) => agg.caption == aggregateName
        );
        const value: number = queryAggregate?.value ?? 0;
        accumulator[aggregateName] += value;
      });
      return accumulator;
    },
    result
  );
};

// @TO DO this is going to be deprecated once API Team updates the backend to use only coach style aggregates do not forget to remove

export const allocateLegacyAggregateTotals = (dataResult: ListResponse, aggregates: string[]) => {
  const result: { [key: string]: number } = aggregates.reduce(
    (acc: { [key: string]: number }, curr: string): { [key: string]: number } => (
      (acc[curr] = 0), acc
    ),
    {}
  );
  return (
    dataResult?.data.reduce(
      (accumulator: { [key: string]: number }, currentValue: any): { [key: string]: number } => {
        aggregates.forEach((aggregateName: string) => {
          const value: number = currentValue?.aggregates[aggregateName] ?? 0;
          accumulator[aggregateName] += value;
        });
        return accumulator;
      },
      result
    ) ?? []
  );
};

type Primitive = string | number | boolean | bigint | symbol | null | undefined;
export type JSONValue = Primitive | JSONObject | JSONArray;
interface JSONObject {
  [key: string]: JSONValue;
}
interface JSONArray extends Array<JSONValue> {}

export function isEqual(a: JSONValue, b: JSONValue): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }

  if (typeof a !== 'object' || typeof b !== 'object') {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (!Array.isArray(a) && !Array.isArray(b)) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!(key in b)) return false;
      if (!isEqual(a[key], b[key])) return false;
    }
    return true;
  }

  return false;
}

/**
 * Converts a given string to a URL-friendly slug.
 * @param text The input string to slugify.
 * @returns A slugified version of the input string.
 */
export function slugify(text: string): string {
  return text
    .toString() // Ensure the input is a string
    .normalize('NFD') // Normalize accented characters into base characters + diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove the diacritic marks
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading and trailing whitespace
    .replace(/[^a-z0-9\s-]/g, '') // Remove all non-alphanumeric characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with a single hyphen
}

export function sanitizeSvg(svg: string, keepClip = false) {
  // 1) Normalize viewBox casing; if missing, infer from clipPath or width/height
  svg = svg.replace(/viewbox=/i, 'viewBox=');

  const vb = svg.match(/\bviewBox\s*=\s*["']([^"']+)["']/);
  if (!vb) {
    // try to infer from clip rect (your case)
    const clip = svg.match(
      /<clipPath[^>]*>[\s\S]*?d="M0 0 L\s*(\d+)\s*0\s*\1\s*(\d+)\s*0\s*\2Z"[\s\S]*?<\/clipPath>/i
    );
    const w = clip
      ? Number(clip[1])
      : Number(svg.match(/\bwidth=["'](\d+)[^"']*["']/i)?.[1] ?? 1247);
    const h = clip
      ? Number(clip[2])
      : Number(svg.match(/\bheight=["'](\d+)[^"']*["']/i)?.[1] ?? 388);
    svg = svg.replace(/<svg/i, `<svg viewBox="0 0 ${w} ${h}"`);
  }

  // 2) Remove problematic sizing/overflow on root
  svg = svg
    .replace(/\swidth=["'][^"']*["']/i, '')
    .replace(/\sheight=["'][^"']*["']/i, '')
    .replace(/\soverflow=["'][^"']*["']/i, '')
    .replace(/\sstyle=["'][^"']*["']/i, (m) => {
      // strip width/height/overflow from style
      const cleaned = m
        .replace(/width\s*:\s*[^;]+;?/gi, '')
        .replace(/height\s*:\s*[^;]+;?/gi, '')
        .replace(/overflow\s*:\s*[^;]+;?/gi, '')
        .replace(/\s*style=\s*["']\s*["']/, ''); // remove empty
      return cleaned;
    });

  // 3) Ensure preserveAspectRatio present
  if (!/\bpreserveAspectRatio\b/i.test(svg)) {
    svg = svg.replace(/<svg/i, '<svg preserveAspectRatio="xMidYMid meet"');
  }

  // 4) Clip handling
  if (!keepClip) {
    svg = svg
      .replace(/<g[^>]*\sclip-path=["'][^"']+["'][^>]*>/i, (tag) =>
        tag.replace(/\sclip-path=["'][^"']+["']/, '')
      )
      .replace(/<clipPath[\s\S]*?<\/clipPath>/gi, '');
  } else {
    // if keeping clip, consider expanding it to match viewBox
    const vb2 = svg.match(/\bviewBox\s*=\s*["']\s*0\s+0\s+(\d+)\s+(\d+)\s*["']/i);
    if (vb2) {
      const [, W, H] = vb2.map(Number) as any;
      svg = svg.replace(
        /(<clipPath[^>]*>)[\s\S]*?(<\/clipPath>)/i,
        `$1<rect x="0" y="0" width="${W}" height="${H}"></rect>$2`
      );
    }
  }

  // 5) Set overflow visible on root to avoid stroke clipping
  svg = svg.replace(/<svg/i, '<svg overflow="visible"');

  return svg;
}

export const formatDisplayNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
